import React, { useState } from "react";
import { Offcanvas } from "react-bootstrap";
import "./DateFilterOffcanvas.css";
import { formatDate } from "../../../../../utils/common";

function DateFilterOffcanvas({ show, handleClose, onApplyDate, disableFuture }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showPicker, setShowPicker] = useState(false);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const daysInMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0,
  ).getDate();

  const firstDay = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1,
  ).getDay();

  const monthYear = currentDate.toLocaleString("default", {
    month: "long",
    year: "numeric",
  });

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  // Filter out future years when disableFuture
  const years = [];
  const maxYear = disableFuture ? today.getFullYear() : 2035;
  for (let y = 2000; y <= maxYear; y++) {
    years.push(y);
  }

  const isCurrentMonth =
    currentDate.getFullYear() === today.getFullYear() &&
    currentDate.getMonth() === today.getMonth();

  const prevMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1),
    );
  };

  const nextMonth = () => {
    // Block navigating to future months when disableFuture
    if (disableFuture && isCurrentMonth) return;
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1),
    );
  };

  const renderDays = () => {
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={"empty-" + i} className="empty_day"></div>);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      const thisDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), i);

      // Skip (don't render) future days when disableFuture
      if (disableFuture && thisDay > today) {
        days.push(<div key={i} className="empty_day"></div>);
        continue;
      }

      const isSelected =
        selectedDate &&
        selectedDate.getDate() === i &&
        selectedDate.getMonth() === currentDate.getMonth() &&
        selectedDate.getFullYear() === currentDate.getFullYear();

      days.push(
        <div
          key={i}
          className={`calendar_day ${isSelected ? "active" : ""}`}
          onClick={() => setSelectedDate(thisDay)}
        >
          {i}
        </div>,
      );
    }

    return days;
  };

  const selectMonth = (monthIndex) => {
    setCurrentDate(new Date(currentDate.getFullYear(), monthIndex, 1));
  };

  const selectYear = (year) => {
    setCurrentDate(new Date(year, currentDate.getMonth(), 1));
  };

  const handleApply = () => {
    if (selectedDate) {
      const d = new Date(selectedDate);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const formatted = `${year}-${month}-${day}`; // "2026-03-16"
      onApplyDate(formatted);
    }
    handleClose();
  };

  // Filter months for the picker — hide future months in current year
  const visibleMonths = months.filter((_, index) => {
    if (!disableFuture) return true;
    if (currentDate.getFullYear() < today.getFullYear()) return true;
    return index <= today.getMonth();
  });

  return (
    <section className="date_filter_parent">
      <Offcanvas
        show={show}
        onHide={handleClose}
        placement="bottom"
        className="date_offcanvas common_offcanvas"
      >
        <Offcanvas.Body>
          <div className="calendar_container">
            {/* Header */}
            <div className="calendar_header" style={{ position: "relative" }}>
              <span onClick={prevMonth}>&lsaquo;</span>

              <h5
                onClick={() => setShowPicker(!showPicker)}
                style={{ cursor: "pointer" }}
              >
                {monthYear.toUpperCase()}
              </h5>

              {/* Hide next arrow on current month when disableFuture */}
              {!(disableFuture && isCurrentMonth) && (
                <span onClick={nextMonth}>&rsaquo;</span>
              )}
              {/* Keep layout balanced when arrow is hidden */}
              {disableFuture && isCurrentMonth && <span />}
            </div>

            {/* Month & Year Picker */}
            {showPicker && (
              <div
                style={{
                  position: "absolute",
                  top: "-55%",
                  left: "0",
                  right: "0",
                  background: "#fff",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                  borderRadius: "8px",
                  padding: "10px",
                  zIndex: 1000,
                  display: "flex",
                  gap: "10px",
                }}
              >
                {/* Month List — future months hidden */}
                <div
                  style={{
                    flex: 1,
                    maxHeight: "160px",
                    overflowY: "auto",
                    scrollbarWidth: "none",
                  }}
                >
                  {visibleMonths.map((m) => {
                    const index = months.indexOf(m);
                    return (
                      <div
                        key={m}
                        className="calendar_day"
                        onClick={() => {
                          selectMonth(index);
                          setShowPicker(false);
                        }}
                      >
                        {m}
                      </div>
                    );
                  })}
                </div>

                {/* Year List — future years hidden */}
                <div
                  style={{
                    flex: 1,
                    maxHeight: "160px",
                    overflowY: "auto",
                    scrollbarWidth: "none",
                  }}
                >
                  {years.map((y) => (
                    <div
                      key={y}
                      className="calendar_day"
                      onClick={() => {
                        selectYear(y);
                        setShowPicker(false);
                      }}
                    >
                      {y}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Week Days */}
            <div className="week_days">
              {["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"].map(
                (day, i) => (
                  <div key={i}>{day}</div>
                ),
              )}
            </div>

            {/* Calendar Grid */}
            <div className="calendar_grid">{renderDays()}</div>

            {/* Buttons */}
            <div className="calendar_buttons">
              <button className="btn-register" onClick={handleClose}>
                Cancel
              </button>
              <button className="btn_register_bg" onClick={handleApply}>
                Apply
              </button>
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </section>
  );
}

export default DateFilterOffcanvas;